import { toast } from "sonner";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Baby, Receipt, Clock, ChevronRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { Card, PageTitle, btnGhost, btnPrimary } from "@/components/portal/ui";
import NeedHelp from "@/components/portal/NeedHelp";

const AGE_BANDS = [
  ["expecting", "Expecting"], ["0-3", "Newborn: 0–3 months"], ["4-6", "Infant: 4–6 months"],
  ["7-12", "Infant: 7–12 months"], ["13-17", "Older infant: 13–17 months"], ["18+", "Toddler: 18+ months"],
];
const CHALLENGES = [
  ["night_waking", "Night wakings & settling (IS-201)"], ["short_naps", "Naps & daily rhythm (NS-101)"],
  ["falling_asleep", "Falling asleep & sleep associations (IS-201)"], ["bedtime", "Bedtime & early waking (IS-401)"],
  ["schedule", "Wake windows & schedules (NS-101)"], ["general", "Foundations of infant sleep (IS-101)"],
];

function LearningPrefs() {
  const [age, setAge] = useState("");
  const [chal, setChal] = useState("");
  useEffect(() => {
    http.get("/portal/membership").then((r) => { setAge(r.data.baby_age_band || ""); setChal(r.data.sleep_challenge || ""); }).catch(() => {});
  }, []);
  const save = async () => {
    await http.put("/portal/membership", { baby_age_band: age || null, sleep_challenge: chal || null }).catch(() => {});
    toast.success("Learning preferences updated.");
  };
  return (
    <Card className="p-6 space-y-4" data-testid="learning-prefs">
      <p className="text-sm text-[#6B665E]">Update these anytime as your child grows or your subject of interest changes; relevant lessons are highlighted in the Learning Center.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="min-w-0">
          <label className="block text-sm text-[#6B665E] mb-1.5">Child's age</label>
          <select data-testid="prefs-age" value={age} onChange={(e) => setAge(e.target.value)} className="w-full min-w-0 border border-[#E5E1D8] rounded-lg px-3 py-2.5 text-sm bg-white text-[#1A1918]">
            <option value="">Select…</option>
            {AGE_BANDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="min-w-0">
          <label className="block text-sm text-[#6B665E] mb-1.5">Subject of greatest interest</label>
          <select data-testid="prefs-challenge" value={chal} onChange={(e) => setChal(e.target.value)} className="w-full min-w-0 border border-[#E5E1D8] rounded-lg px-3 py-2.5 text-sm bg-white text-[#1A1918]">
            <option value="">Select…</option>
            {CHALLENGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <button data-testid="prefs-save" onClick={save} className={btnPrimary}>Save preferences</button>
    </Card>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const LINKS = [
    ["My Sleep Profile", "Your child's sleep profile & details", Baby, "/portal/profile", "account-link-profile"],
    ["Purchases & Receipts", "Your orders, payments and service history", Receipt, "/portal/purchases", "account-link-purchases"],
    ["Timeline", "Your full activity history", Clock, "/portal/timeline", "account-link-timeline"],
  ];

  return (
    <div>
      <PageTitle eyebrow="Account" title="My Account" sub="You are the parent/guardian account holder. Children are managed as profiles, never as logins." />
      <Card className="p-7 max-w-2xl space-y-5">
        <div><div className="text-xs text-[#8A998C] uppercase tracking-wide mb-1">Name</div><div className="text-[#1A1918]">{user?.name}</div></div>
        <div><div className="text-xs text-[#8A998C] uppercase tracking-wide mb-1">Email</div><div className="text-[#1A1918]">{user?.email}</div></div>
      </Card>

      <div className="max-w-2xl mt-6">
        <div className="eyebrow text-[#8A998C] mb-3">Profile, purchases & history</div>
        <div className="space-y-2">
          {LINKS.map(([title, sub, Icon, to, tid]) => (
            <button key={to} data-testid={tid} onClick={() => nav(to)}
              className="w-full bg-white border border-[#E5E1D8] rounded-xl px-5 py-4 flex items-center gap-4 hover:border-[#9F634E]/40 transition-colors text-left">
              <span className="w-10 h-10 rounded-full grid place-items-center bg-[#F3EFE9] shrink-0"><Icon size={18} className="text-[#9F634E]" /></span>
              <span className="flex-1"><span className="block text-[#1A1918] text-[15px] font-medium">{title}</span><span className="block text-[#8A998C] text-xs">{sub}</span></span>
              <ChevronRight size={18} className="text-[#C8C1B5] shrink-0" />
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mt-6">
        <div className="eyebrow text-[#8A998C] mb-3 flex items-center gap-1.5"><Sparkles size={14} /> Learning preferences</div>
        <LearningPrefs />
      </div>

      <div className="max-w-2xl mt-6">
        <div className="eyebrow text-[#8A998C] mb-3">Privacy & your data</div>
        <Card className="p-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => toast.success("Data export requested. We'll email a copy of your account data.")} className={btnGhost}>Request data export</button>
            <button onClick={() => toast.success("Deletion request submitted for review.")} className={btnGhost}>Request account deletion</button>
          </div>
          <p className="text-xs text-[#8A998C] leading-relaxed">Private service data (intake, reports, plans, logs, notes) is kept separate from marketing and analytics. Sensitive details are never shared with analytics tools.</p>
        </Card>
      </div>

      <div className="max-w-2xl mt-6">
        <div className="eyebrow text-[#8A998C] mb-3">Support</div>
        <NeedHelp />
      </div>

      <button onClick={async () => { await logout(); nav("/"); }} className={`${btnPrimary} mt-8`}>Sign out</button>
    </div>
  );
}
